import { useMemo } from 'react';
import Iconify from 'src/components/iconify/iconify';
// components
import SvgColor from 'src/components/svg-color';
import useAuth from 'src/routes/hooks/use-auth';
// routes
import { paths } from 'src/routes/paths';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  alert: icon('ic_alert'),
  people: <Iconify icon="mdi:people" width={24} height={24} />,
  web: <Iconify icon="mdi:web" width={24} height={24} />,
  code: <Iconify icon="mdi:barcode" width={24} height={24} />,
  appCode: <Iconify icon="mdi:cellphone-check" width={24} height={24} />,
  room: icon('ic_room')
};

// ----------------------------------------------------------------------

// 메뉴 아이템의 타입을 명시적으로 정의
type SubMenuItem = {
  title: string;
  path: string;
  tabAccessCodeId?: string;
};

type MenuItem = {
  title: string;
  path: string;
  icon: React.ReactElement;
  tabAccessCodeId?: string;
  children?: SubMenuItem[];
};

type NavSection = {
  subheader: string;
  tabAccessCodeId?: string;
  items: MenuItem[];
};

export function useNavData() {
  const { logout } = useAuth();
  // const auth = useRecoilValue(authStore);

  // FIXME: 임시 주석 처리
  // const { data: tabInfo } = useSuspenseQuery({
  //   queryKey: ['tabAccess'],
  //   queryFn: async () => {
  //     const res = await authFetch.tabAccess();
  //     if (!res) {
  //       logout();
  //       return null;
  //     }
  //     return res?.data.data;
  //   }
  // });

  const data = useMemo(
    () => [
      {
        subheader: '',
        tabAccessCodeId: '021',
        items: [
          {
            title: '회원 관리',
            path: paths.member.root,
            icon: ICONS.user
          },
          {
            title: '대시보드',
            path: paths.dashboard.root,
            icon: ICONS.dashboard
          },
          {
            title: '결제 관리',
            path: paths.payment.root,
            icon: ICONS.banking,
            children: [
              { title: '앱 결제 내역', path: paths.payment.app },
              { title: '코드 등록 내역', path: paths.payment.code }
            ]
          },
          {
            title: '앱·웹 공용 코드 관리',
            path: paths.code.root,
            icon: ICONS.code,
            children: [
              { title: '1급 필기 코드', path: paths.code.web('GRADE1_WRITTEN') },
              { title: '1급 실기 코드', path: paths.code.web('GRADE1_PRACTICAL') },
              { title: '2급 필기 코드', path: paths.code.web('GRADE2_WRITTEN') },
              { title: '2급 실기 코드', path: paths.code.web('GRADE2_PRACTICAL') }
            ]
          },
          {
            title: '앱 전용 코드 관리',
            path: paths.appCode.root,
            icon: ICONS.appCode,
            children: [
              { title: '앱 1급', path: paths.appCode.firstLevel },
              { title: '앱 2급', path: paths.appCode.secondLevel }
            ]
          },
          {
            title: '문제 등록 관리',
            path: paths.exam.root,
            icon: ICONS.file,
            children: [
              { title: '1급 문제 관리', path: paths.exam.firstLevelMasterList },
              { title: '2급 문제 관리', path: paths.exam.secondLevelMasterList }
            ]
          },
          {
            title: '통계',
            path: paths.statistics.root,
            icon: ICONS.analytics
          },
          {
            title: '약관 관리',
            path: paths.terms.root,
            icon: ICONS.blog
          },
          {
            title: '관리자 계정 관리',
            path: paths.adminAccount.root,
            icon: ICONS.lock
          }
        ]
      },
      {
        subheader: 'WEB 서비스',
        items: [
          {
            title: '웹(WEB) 관리',
            path: paths.webAdmin.root,
            icon: ICONS.web,
            children: [
              { title: '웹 대시보드', path: paths.webAdmin.dashboard },
              { title: '웹 회원 관리', path: paths.webAdmin.members },
              { title: '자료 등록 관리', path: paths.webAdmin.content('materials') },
              { title: '다운로드 이력', path: paths.webAdmin.downloads },
              { title: '워터마크 설정', path: paths.webAdmin.watermark },
              { title: '강의 관리', path: paths.webAdmin.lectures },
              { title: '교재 관리', path: paths.webAdmin.textbooks },
              { title: '고객센터 관리', path: paths.webAdmin.customerCenter() },
              { title: '웹 코드 등록 내역', path: paths.webAdmin.codeRegistrations },
              { title: '회차(개정기간) 관리', path: paths.webAdmin.revisions },
              { title: '웹 통계', path: paths.webAdmin.stats },
              { title: '웹 약관 관리', path: paths.webAdmin.terms }
            ]
          }
        ]
      }
    ],
    []
  );

  const filteredData = useMemo(() => {
    // DM_MASTER 역할이면 모든 접근 권한 부여

    // FIXME: 임시 주석 처리
    // if (auth.role === MemberRole.DM_MASTER) {
    return data;
    // }

    // 먼저 상위 메뉴 필터링
    const filteredItems = data.filter((item: NavSection) => {
      if (item.tabAccessCodeId) {
        // @ts-ignore
        return tabInfo?.some((tab) => tab.tabAccessCodeId === item.tabAccessCodeId);
      }

      // 상위 항목에 tabAccessCodeId가 없고 items가 있는 경우
      if (item.items && item.items.length > 0) {
        // 하위 항목 중 접근 가능한 것이 하나라도 있으면 표시
        return item.items.some((subItem: MenuItem) => {
          if ('tabAccessCodeId' in subItem && subItem.tabAccessCodeId) {
            // @ts-ignore
            return tabInfo?.some((tab) => tab.tabAccessCodeId === subItem.tabAccessCodeId);
          }
          return true;
        });
      }

      return true;
    });

    // 다음으로 하위 메뉴도 필터링하여 새 객체 생성
    return filteredItems.map((item: NavSection) => {
      if (item.items && item.items.length > 0) {
        const filteredSubItems = item.items.filter((subItem: MenuItem) => {
          if ('tabAccessCodeId' in subItem && subItem.tabAccessCodeId) {
            // @ts-ignore
            return tabInfo?.some((tab) => tab.tabAccessCodeId === subItem.tabAccessCodeId);
          }
          return true;
        });

        return { ...item, items: filteredSubItems };
      }
      return item;
    });
  }, [
    data

    // tabInfo,

    // auth.role
  ]);
  return filteredData;
}
